/**
 * Modal do par natureza × fonte de `INVOICE_PAYMENT_ACTION` (AF-4126, Anexo 1): padrão da fatura
 * anterior na liquidação e no pagamento parcial, e a fatura residual do parcial. "Sim, Automático"
 * abre três checkboxes que compõem o valor (`AUTO` + `_UNCOLLECT` / `_BOLETO` / `_HIDDEN`).
 *
 * Aplicar altera só o par na grade; quem grava é o Salvar da tela.
 */
import { useState } from 'react';
import ACheckbox from '../../../atoms/ACheckbox';
import ActionButton from '../../../atoms/ActionButton';
import SelectTextField from '../../../molecules/SelectTextField';
import Dialog from '../../../organisms/Dialog';
import {
    composeResidual, decomposeResidual, ResidualControls, ResidualMode,
} from '../services/businessPropertyRules';
import { ActionPair, PARENT_OPTIONS, PARTIAL, RESIDUAL_MODE_OPTIONS, TOTAL } from './invoicePaymentActionModel';

interface Props {
    reference: string;
    source: string;
    pair: ActionPair | null;
    onClose: () => void;
    onApply: (pair: ActionPair) => void;
}

interface Draft {
    totalParent: string;
    partialParent: string;
    residual: ResidualControls;
}

const SECTION = 'flex flex-col gap-7';
const SECTION_TITLE = 'text-sm font-semibold text-gray-700 dark:text-gray-200';

const toDraft = (pair: ActionPair): Draft => ({
    totalParent: pair[TOTAL].parent ?? '',
    partialParent: pair[PARTIAL].parent ?? '',
    residual: decomposeResidual(pair[PARTIAL].residual),
});

const toPair = (draft: Draft): ActionPair => ({
    [TOTAL]: { parent: draft.totalParent || null },
    [PARTIAL]: { parent: draft.partialParent || null, residual: composeResidual(draft.residual) },
});

const FLAGS: { key: keyof Omit<ResidualControls, 'mode'>; label: string }[] = [
    { key: 'activeCollection', label: 'Cobrança Ativa' },
    { key: 'boleto', label: 'Boleto' },
    { key: 'hidden', label: 'Ocultar' },
];

const InvoicePaymentActionPairModal = ({ reference, source, pair, onClose, onApply }: Props) => {
    const [draft, setDraft] = useState<Draft | null>(pair ? toDraft(pair) : null);

    if (!draft) return null;

    const residual = draft.residual;
    const body = (
        <div className="flex flex-col gap-6 px-6 pt-4 pb-6 w-[440px] max-w-full">
            <section className={SECTION}>
                <p className={SECTION_TITLE}>Padrões quando Liquidação</p>
                <SelectTextField
                    id="pair-total-parent"
                    label="Fatura Anterior"
                    items={PARENT_OPTIONS}
                    emptyLabel="Selecione"
                    value={draft.totalParent}
                    onChange={e => setDraft({ ...draft, totalParent: e.target.value })}
                />
            </section>
            <section className={`${SECTION} border-t border-gray-200 dark:border-slate-700 pt-5`}>
                <p className={SECTION_TITLE}>Padrões quando Pagamento Parcial</p>
                <SelectTextField
                    id="pair-partial-parent"
                    label="Fatura Anterior"
                    items={PARENT_OPTIONS}
                    emptyLabel="Selecione"
                    value={draft.partialParent}
                    onChange={e => setDraft({ ...draft, partialParent: e.target.value })}
                />
                <SelectTextField
                    id="pair-residual-mode"
                    label="Invoice Residual?"
                    items={RESIDUAL_MODE_OPTIONS}
                    hideEmptyOption
                    value={residual.mode}
                    onChange={e => setDraft({ ...draft, residual: { ...residual, mode: e.target.value as ResidualMode } })}
                />
                {residual.mode === 'AUTO' && (
                    <div className="flex flex-wrap gap-6 -mt-2 text-sm text-gray-700 dark:text-gray-200">
                        {FLAGS.map(({ key, label }) => (
                            <div key={key} className="flex items-center gap-2">
                                <ACheckbox
                                    ariaLabel={label}
                                    checked={residual[key]}
                                    onChange={checked => setDraft({ ...draft, residual: { ...residual, [key]: checked } })}
                                />
                                <span>{label}</span>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );

    return (
        <Dialog
            id="invoice-payment-action-pair"
            show
            onClose={onClose}
            header={`${reference} × ${source}`}
            body={body}
            footer={(
                <div className="flex gap-2">
                    <ActionButton variant="secondary" onClick={onClose}>Cancelar</ActionButton>
                    <ActionButton onClick={() => onApply(toPair(draft))}>Aplicar</ActionButton>
                </div>
            )}
        />
    );
};

export default InvoicePaymentActionPairModal;
